/**
 * FiIncomeCostChart — hero chart of the FI cockpit: revenue (stacked by
 * offering) vs total cost (cogs + overhead) vs gross profit, over time.
 */
import { useMemo } from 'react'
import {
  Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart,
} from 'recharts'
import type { FiPeriod } from './fiTypes'
import { formatCurrency, formatCompact } from './fiFormat'

interface FiIncomeCostChartProps {
  trend: FiPeriod[]
}

interface ChartRow {
  period: string
  revenuePackage: number
  revenueConsulting: number
  totalCost: number
  grossProfit: number
}

interface TooltipPayloadEntry {
  value?: number
  dataKey?: string
}

function ChartTooltip({ active, payload, label }: {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string
}) {
  if (!active || !payload || payload.length === 0) return null
  const get = (key: string) => payload.find(p => p.dataKey === key)?.value || 0
  const pkg = get('revenuePackage')
  const consulting = get('revenueConsulting')
  const cost = get('totalCost')
  const profit = get('grossProfit')
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3 min-w-[220px]">
      <p className="font-semibold text-white mb-2">{label}</p>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="flex items-center gap-1.5 text-gray-300">
            <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Paquete
          </span>
          <span className="text-white font-medium">{formatCurrency(pkg)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="flex items-center gap-1.5 text-gray-300">
            <span className="w-2.5 h-2.5 rounded-sm bg-purple-500" /> Consultoría
          </span>
          <span className="text-white font-medium">{formatCurrency(consulting)}</span>
        </div>
        <div className="border-t border-gray-600 pt-1 flex justify-between gap-4">
          <span className="text-gray-300 font-medium">Ingresos</span>
          <span className="text-white font-bold">{formatCurrency(pkg + consulting)}</span>
        </div>
        <div className="flex justify-between gap-4 text-red-400">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-0.5 bg-red-400 inline-block" /> Costo total
          </span>
          <span className="font-medium">{formatCurrency(cost)}</span>
        </div>
        <div className="flex justify-between gap-4 text-green-400">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-0.5 bg-green-400 inline-block" /> Utilidad bruta
          </span>
          <span className="font-medium">{formatCurrency(profit)}</span>
        </div>
      </div>
    </div>
  )
}

export default function FiIncomeCostChart({ trend }: FiIncomeCostChartProps) {
  const data: ChartRow[] = useMemo(() => trend.map(p => ({
    period: p.label,
    revenuePackage: p.revenuePackage,
    revenueConsulting: p.revenueConsulting,
    totalCost: p.cogs + p.overhead,
    grossProfit: p.grossProfit,
  })), [trend])

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Ingresos vs Costos</h3>
        <p className="text-gray-400 text-sm">Paquete + Consultoría, contra costo total y utilidad bruta</p>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Sin datos históricos suficientes.</div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
              <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }}
                tickFormatter={v => formatCompact(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="square" iconSize={10}
                formatter={(value: string) => (
                  <span className="text-gray-400 text-xs">
                    {value === 'revenuePackage' ? 'Paquete'
                      : value === 'revenueConsulting' ? 'Consultoría'
                      : value === 'totalCost' ? 'Costo Total'
                      : value === 'grossProfit' ? 'Utilidad Bruta' : value}
                  </span>
                )}
              />
              <Bar dataKey="revenuePackage" stackId="revenue" fill="#3b82f6" radius={[0, 0, 0, 0]} name="revenuePackage" />
              <Bar dataKey="revenueConsulting" stackId="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="revenueConsulting" />
              <Line type="monotone" dataKey="totalCost" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="totalCost" />
              <Line type="monotone" dataKey="grossProfit" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="grossProfit" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
